/**
 * All activity/relationship normalization and "compute from activity list" metrics live here.
 * Chart display constants live in overview/helpers.
 */

import type { ScheduleActivity } from '@/types';

// ---------------------------------------------------------------------------
// Data normalization and counts (moved from overview/helpers)
// ---------------------------------------------------------------------------

export function normalizeActivities(response: { activities?: unknown[]; data?: { activities?: unknown[] } } | unknown[]): ScheduleActivity[] {
  if (Array.isArray(response)) return response as ScheduleActivity[];
  if (response && typeof response === 'object' && 'activities' in response) {
    return ((response as { activities: unknown[] }).activities ?? []) as ScheduleActivity[];
  }
  if (response && typeof response === 'object' && 'data' in response) {
    const inner = (response as { data: unknown }).data;
    if (inner && typeof inner === 'object' && inner !== null && 'activities' in inner) {
      return ((inner as { activities: unknown[] }).activities ?? []) as ScheduleActivity[];
    }
  }
  return [];
}

/**
 * Count activities missing predecessors or successors.
 * API returns relationships with predecessor_id/successor_id (or pred_activity_id/succ_activity_id, source/target).
 */
export function countMissingRelations(
  relationships: Record<string, unknown>[],
  activities: ScheduleActivity[]
): { pred: number; succ: number } {
  const hasSuccessor = new Set<string>();
  const hasPredecessor = new Set<string>();
  for (const r of relationships) {
    const predId = String(r.predecessor_id ?? r.pred_activity_id ?? r.source ?? '');
    const succId = String(r.successor_id ?? r.succ_activity_id ?? r.target ?? '');
    if (predId) hasSuccessor.add(predId);
    if (succId) hasPredecessor.add(succId);
  }

  const countable = activities.filter((a) => {
    const taskType = (a as Record<string, unknown>).task_type as string | undefined;
    if (taskType === 'TT_LOE' || taskType === 'TT_WBS') return false;
    const type = a.type ?? '';
    if (['Finish Milestone', 'TT_FinMile'].includes(type)) return false;
    if (['Start Milestone', 'TT_Mile'].includes(type)) return false;
    return true;
  });

  let pred = 0;
  let succ = 0;
  for (const a of countable) {
    const taskId = String((a as Record<string, unknown>).task_id ?? a.id ?? '');
    if (!taskId) continue;
    if (!hasPredecessor.has(taskId)) pred++;
    if (!hasSuccessor.has(taskId)) succ++;
  }

  if (import.meta.env.DEV && pred === succ && relationships.length > 0 && pred > 0) {
    console.warn(
      `[Charts] Missing preds (${pred}) equals missing succs (${succ}) with ${relationships.length} relationships — verify field names.`
    );
  }

  return { pred, succ };
}

/** Count relationships with excessive lag. Threshold: > 40 hours = > 5 working days. */
export function countExcessiveLags(relationships: Record<string, unknown>[]): number {
  const LAG_THRESHOLD_HOURS = 40;
  return relationships.filter((r) => {
    const lagHours = Number(r.lag_hours ?? 0);
    return Math.abs(lagHours) > LAG_THRESHOLD_HOURS;
  }).length;
}

export function countHardConstraints(activities: ScheduleActivity[]): number {
  const DEFAULT = new Set(['', 'CS_MEO', 'CS_ALAP']);
  return activities.filter((a) => {
    const ct = (a as ScheduleActivity & { primary_constraint_type?: string }).primary_constraint_type;
    return ct != null && ct !== '' && !DEFAULT.has(ct);
  }).length;
}

/** Build baseline/target date map from activity target finish. */
export function buildBaselineMap(activities: ScheduleActivity[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const a of activities) {
    const id = String(a.id ?? (a as Record<string, unknown>).task_id ?? '');
    const raw = a as Record<string, unknown>;
    const targetEnd = (raw.target_end_date ?? raw.target_finish_date ?? a.target_finish_date) as string | undefined;
    if (id && targetEnd && String(targetEnd).trim() !== '') {
      map[id] = targetEnd;
    }
  }
  return map;
}

// ---------------------------------------------------------------------------
// Float / completion / milestone helpers
// ---------------------------------------------------------------------------

/**
 * Raw float hours from XER (total_float_hr_cnt). Use for display and sorting.
 * We do not convert to days: days depend on the task's calendar (hours/day), which we do not have per task.
 */
export function getFloatHours(a: ScheduleActivity | Record<string, unknown>): number {
  const hrs = (a as Record<string, unknown>).total_float_hr_cnt;
  if (hrs !== undefined && hrs !== null) return Number(hrs);
  return 0;
}

/** @deprecated Use getFloatHours; days depend on task calendar. Only for approximate buckets (e.g. 80 hr ≈ 10 "8h-days"). */
export function getFloatDays(a: ScheduleActivity | Record<string, unknown>): number {
  return getFloatHours(a) / 8;
}

/** Activity is completed (status or 100% complete) */
export function isCompleted(a: ScheduleActivity | Record<string, unknown>): boolean {
  const status = (a as ScheduleActivity).status ?? '';
  if (['Completed', 'TK_Complete', 'Complete'].includes(status)) return true;
  const code = (a as Record<string, unknown>).status_code;
  if (code === 'TK_Complete' || code === 'TK_Finish') return true;
  const pct = (a as ScheduleActivity).percent_complete ?? (a as ScheduleActivity).effective_percent_complete
    ?? (a as ScheduleActivity).phys_complete_pct ?? (a as Record<string, unknown>).physical_percent_complete;
  if (pct != null) {
    const n = typeof pct === 'number' ? pct : parseFloat(String(pct));
    if (!isNaN(n) && n >= 100) return true;
  }
  return false;
}

/** Activity is milestone: type OR duration=0 OR known milestone flag */
export function isMilestone(a: ScheduleActivity | Record<string, unknown>): boolean {
  const type = (a as ScheduleActivity).type ?? '';
  if (['Milestone', 'Finish Milestone', 'Start Milestone', 'TT_Mile'].includes(type)) return true;
  if ((a as Record<string, unknown>).is_milestone === true) return true;
  const dur = (a as ScheduleActivity).duration ?? (a as ScheduleActivity).original_duration ?? (a as Record<string, unknown>).target_drtn_hr_cnt;
  const durationNum = typeof dur === 'number' ? dur : dur != null ? Number(dur) : null;
  if (durationNum === 0) return true;
  return false;
}

const FLOAT_BUCKETS = ['<0', '0', '1-7', '8-14', '15-30', '31-44', '>44'] as const;
const BUCKET_DAYS: [string, number, number][] = [
  ['<0', -Infinity, -0.001],
  ['0', 0, 0],
  ['1-7', 0.001, 7],
  ['8-14', 7.001, 14],
  ['15-30', 14.001, 30],
  ['31-44', 30.001, 44],
  ['>44', 44.001, Infinity],
];

export interface FloatDistributionBucket {
  bucket: string;
  count: number;
}

export function computeFloatDistribution(activities: ScheduleActivity[]): FloatDistributionBucket[] {
  const counts: Record<string, number> = {};
  FLOAT_BUCKETS.forEach((b) => { counts[b] = 0; });
  activities.forEach((a) => {
    const days = getFloatDays(a);
    for (const [bucket, low, high] of BUCKET_DAYS) {
      if (days >= low && days <= high) {
        counts[bucket] = (counts[bucket] ?? 0) + 1;
        break;
      }
    }
  });
  return FLOAT_BUCKETS.map((bucket) => ({ bucket, count: counts[bucket] ?? 0 }));
}

export interface ScheduleMetrics {
  minFloatDays: number;
  negativeFloatCount: number;
  criticalCount: number;
  nearCriticalCount: number;
  /** Union: not completed and float <= 5 days */
  criticalOrNearCriticalCount: number;
  highFloatPct: number;
  floatDistribution: FloatDistributionBucket[];
  projectFinishDate: string | null;
  criticalPathDurationDays: number | null;
}

export function computeScheduleMetrics(activities: ScheduleActivity[]): ScheduleMetrics {
  if (activities.length === 0) {
    return {
      minFloatDays: 0,
      negativeFloatCount: 0,
      criticalCount: 0,
      nearCriticalCount: 0,
      criticalOrNearCriticalCount: 0,
      highFloatPct: 0,
      floatDistribution: computeFloatDistribution([]),
      projectFinishDate: null,
      criticalPathDurationDays: null,
    };
  }
  const nonLoe = activities.filter((a) => (a as Record<string, unknown>).task_type !== 'TT_LOE');
  const floatDays = nonLoe.map(getFloatDays);
  const minFloatDays = floatDays.length ? Math.min(...floatDays) : 0;
  const negativeFloatCount = floatDays.filter((d) => d < 0).length;
  // Critical: on the P6 driving path OR zero float
  const criticalCount = nonLoe.filter((a) => {
    const c = (a as ScheduleActivity).critical ?? (a as Record<string, unknown>).is_critical;
    if (c === true) return true;
    return getFloatHours(a) <= 0;
  }).length;
  // Near-critical: NOT critical AND float within 5 working days (40 hr)
  const nearCriticalCount = nonLoe.filter((a) => {
    if (isCompleted(a)) return false;
    const c = (a as ScheduleActivity).critical ?? (a as Record<string, unknown>).is_critical;
    if (c === true) return false;
    const hrs = getFloatHours(a);
    return hrs > 0 && hrs <= NEAR_CRITICAL_FLOAT_HOURS;
  }).length;
  const criticalOrNearCriticalCount = criticalCount + nearCriticalCount;
  const highFloatCount = nonLoe.filter((a) => getFloatDays(a) > 44).length;
  const highFloatPct = nonLoe.length ? (highFloatCount / nonLoe.length) * 100 : 0;
  const floatDistribution = computeFloatDistribution(nonLoe);

  const startDates = activities
    .map((a) => (a as ScheduleActivity).early_start_date ?? (a as ScheduleActivity).start_date ?? (a as Record<string, unknown>).target_start_date)
    .filter(Boolean) as string[];
  const endDates = activities
    .map((a) => (a as ScheduleActivity).early_end_date ?? (a as ScheduleActivity).end_date ?? (a as Record<string, unknown>).target_end_date)
    .filter(Boolean) as string[];
  const projectStartDate = startDates.length
    ? startDates.reduce((earliest, d) => (d < earliest ? d : earliest), startDates[0])
    : null;
  const projectFinishDate = endDates.length
    ? endDates.reduce((latest, d) => (d > latest ? d : latest), endDates[0])
    : null;
  let criticalPathDurationDays: number | null = null;
  if (projectStartDate && projectFinishDate) {
    try {
      const ms = new Date(projectFinishDate).getTime() - new Date(projectStartDate).getTime();
      criticalPathDurationDays = Math.round(ms / (24 * 60 * 60 * 1000));
    } catch {
      criticalPathDurationDays = null;
    }
  }

  return {
    minFloatDays,
    negativeFloatCount,
    criticalCount,
    nearCriticalCount,
    criticalOrNearCriticalCount,
    highFloatPct: Math.round(highFloatPct * 10) / 10,
    floatDistribution,
    projectFinishDate,
    criticalPathDurationDays,
  };
}

export interface WBSHotspotRow {
  wbs_id: string;
  wbs_name: string;
  wbs_short_name: string;
  total_activities: number;
  avgFloatDays: number;
  minFloatDays: number;
  criticalNearCriticalCount: number;
  criticalNearCriticalPct: number;
  overdueCount: number;
  /** Deterministic hotspot score: higher = worse. Used for default sort. */
  hotspotScore: number;
}

export function computeWBSHotspots(activities: ScheduleActivity[]): WBSHotspotRow[] {
  const byWbs = new Map<string, ScheduleActivity[]>();
  activities.forEach((a) => {
    const wbsId = String((a as ScheduleActivity).wbs_id ?? '');
    if (!byWbs.has(wbsId)) byWbs.set(wbsId, []);
    byWbs.get(wbsId)!.push(a);
  });
  const now = new Date();
  const rows: WBSHotspotRow[] = [];
  byWbs.forEach((list, wbs_id) => {
    const floatDays = list.map(getFloatDays);
    const avgFloatDays = floatDays.length ? floatDays.reduce((s, d) => s + d, 0) / floatDays.length : 0;
    const minFloatDays = floatDays.length ? Math.min(...floatDays) : 0;
    const criticalNearCriticalCount = list.filter((a) => {
      if (isCompleted(a)) return false;
      const d = getFloatDays(a);
      return d <= 5;
    }).length;
    const criticalNearCriticalPct = list.length ? (criticalNearCriticalCount / list.length) * 100 : 0;
    const overdueCount = list.filter((a) => {
      if (isCompleted(a)) return false;
      const end = (a as ScheduleActivity).early_end_date ?? (a as Record<string, unknown>).early_end_date as string | undefined;
      return end ? new Date(end) < now : false;
    }).length;
    const first = list[0] as ScheduleActivity & { wbs_name?: string; wbs_short_name?: string };
    const shortName = (first as Record<string, unknown>)?.wbs_short_name ?? first?.wbs_name ?? wbs_id;
    // Score: higher = worse. -minFloat*2 (negative float → high; huge positive float → large negative score),
    // + criticalNearCriticalPct*0.5, + overdueCount*3. Negative scores = "cold" (safe) WBS.
    const hotspotScore = -minFloatDays * 2 + criticalNearCriticalPct * 0.5 + overdueCount * 3;
    rows.push({
      wbs_id,
      wbs_name: first?.wbs_name ?? wbs_id,
      wbs_short_name: typeof shortName === 'string' ? shortName : String(shortName ?? wbs_id),
      total_activities: list.length,
      avgFloatDays: Math.round(avgFloatDays * 10) / 10,
      minFloatDays: Math.round(minFloatDays * 10) / 10,
      criticalNearCriticalCount,
      criticalNearCriticalPct: Math.round(criticalNearCriticalPct * 10) / 10,
      overdueCount,
      hotspotScore: Math.round(hotspotScore * 10) / 10,
    });
  });
  return rows.sort((a, b) => b.hotspotScore - a.hotspotScore);
}

/**
 * Near-critical threshold: 40 hr = 5 working days (8 h/day).
 * Matches backend NEAR_CRITICAL_THRESHOLD_HOURS and analyticsThresholds.ts (≤5d).
 *
 * The table still shows activities up to CRITICAL_TABLE_FLOAT_HOURS (80 hr ≈ 10d)
 * for wider context, but only those ≤ 40 hr are badged "Near-Critical".
 */
export const NEAR_CRITICAL_FLOAT_HOURS = 40;
/** Wider table cut-off: show activities up to ~10 working days for context. */
export const CRITICAL_TABLE_FLOAT_HOURS = 80;

/** Classification for each activity row in the critical/near-critical table. */
export type CriticalityBand = 'critical' | 'near-critical' | 'monitor';

export interface CriticalActivityRow extends ScheduleActivity {
  /** Raw XER total_float_hr_cnt (hours). Display and filter in hours; days depend on task calendar. */
  floatHours: number;
  /** Approximate days (÷8) for backward compatibility / bucket labels only. */
  floatDays: number;
  /**
   * Classification:
   *  - "critical"       → on the driving path OR float ≤ 0
   *  - "near-critical"  → 0 < float ≤ NEAR_CRITICAL_FLOAT_HOURS (40 hr / 5d)
   *  - "monitor"        → NEAR_CRITICAL < float ≤ CRITICAL_TABLE_FLOAT_HOURS (80 hr / 10d)
   */
  criticalityBand: CriticalityBand;
}

function classifyCriticality(a: ScheduleActivity, floatHours: number): CriticalityBand {
  // Driving-path flag from P6
  const onDrivingPath =
    (a as Record<string, unknown>).is_critical === true ||
    (a as Record<string, unknown>).driving_path_flag === 'Y' ||
    (a as ScheduleActivity & { critical?: boolean }).critical === true;

  if (onDrivingPath || floatHours <= 0) return 'critical';
  if (floatHours <= NEAR_CRITICAL_FLOAT_HOURS) return 'near-critical';
  return 'monitor';
}

export function computeCriticalActivities(activities: ScheduleActivity[]): CriticalActivityRow[] {
  const list = activities
    .filter((a) => !isCompleted(a))
    .map((a) => {
      const floatHours = getFloatHours(a);
      return {
        ...a,
        floatHours,
        floatDays: floatHours / 8,
        criticalityBand: classifyCriticality(a, floatHours),
      };
    })
    .filter((a) => a.floatHours <= CRITICAL_TABLE_FLOAT_HOURS)
    .sort((a, b) => a.floatHours - b.floatHours);
  return list;
}

/** Milestone risk: unknown when current planned date is missing */
export interface MilestoneRow {
  id: string;
  name: string;
  wbs_id: string;
  wbs_name?: string;
  baselineOrOriginalDate: string | null;
  currentPlannedDate: string | null;
  varianceDays: number | null;
  riskBand: 'ok' | 'at_risk' | 'overdue' | 'unknown';
}

/**
 * Overdue: current_planned < data_date AND not complete.
 * At risk: variance > 0 (late vs baseline) OR negative/near-critical float.
 * OK: valid planned date, not overdue, not at risk.
 * Unknown: missing current planned date (never mark overdue when current is "—").
 */
export function computeMilestones(
  activities: ScheduleActivity[],
  baselineOrOriginalByActivityId?: Record<string, string>,
  dataDate?: Date
): MilestoneRow[] {
  const milestones = activities.filter(isMilestone);
  const now = dataDate ?? new Date();
  return milestones.map((a) => {
    const raw = a as Record<string, unknown>;
    const currentRaw = (a as ScheduleActivity).early_end_date ?? (a as ScheduleActivity).end_date ?? raw.target_end_date ?? raw.target_finish_date ?? (a as ScheduleActivity).target_finish_date;
    const current: string | null = currentRaw != null && String(currentRaw).trim() !== '' ? String(currentRaw) : null;
    const baselineOrOriginal = baselineOrOriginalByActivityId?.[(a as ScheduleActivity).id ?? (raw.task_id as string)] ?? null;
    let varianceDays: number | null = null;
    if (current && baselineOrOriginal) {
      try {
        varianceDays = Math.round((new Date(current).getTime() - new Date(baselineOrOriginal).getTime()) / (24 * 60 * 60 * 1000));
      } catch {
        varianceDays = null;
      }
    }
    const completed = isCompleted(a);
    const floatDays = getFloatDays(a);
    const hasValidCurrent = current != null;
    let riskBand: MilestoneRow['riskBand'];
    if (!hasValidCurrent) {
      riskBand = 'unknown';
    } else {
      const currentDate = new Date(current);
      const isOverdue = currentDate < now && !completed;
      const isAtRisk = (varianceDays != null && varianceDays > 0) || floatDays < 0 || (!completed && floatDays <= 5);
      riskBand = isOverdue ? 'overdue' : isAtRisk ? 'at_risk' : 'ok';
    }
    return {
      id: String((a as ScheduleActivity).id ?? raw.task_id ?? ''),
      name: (a as ScheduleActivity).name ?? '',
      wbs_id: String((a as ScheduleActivity).wbs_id ?? ''),
      wbs_name: (a as ScheduleActivity).wbs_name,
      baselineOrOriginalDate: baselineOrOriginal,
      currentPlannedDate: current,
      varianceDays,
      riskBand,
    };
  }).sort((a, b) => (b.varianceDays ?? 0) - (a.varianceDays ?? 0));
}

export interface QualityCheckRow {
  id: string;
  label: string;
  value: number;
  threshold: string;
  status: 'pass' | 'warn' | 'fail';
  viewOffendersHref?: string;
}

export function computeQualityChecks(
  activities: ScheduleActivity[],
  missingPredecessorsCount: number,
  missingSuccessorsCount: number,
  constraintsCount: number,
  excessiveLagsCount: number
): QualityCheckRow[] {
  const nonLoe = activities.filter((a) => (a as Record<string, unknown>).task_type !== 'TT_LOE');
  const total = nonLoe.length;
  const highFloatCount = nonLoe.filter((a) => getFloatDays(a) > 44).length;
  const highFloatPct = total ? (highFloatCount / total) * 100 : 0;
  const longDurationDays = 44;
  const longCount = nonLoe.filter((a) => {
    // XER stores duration in hours (target_drtn_hr_cnt); 8 hr = 1 working day
    const hrsRaw = (a as Record<string, unknown>).target_drtn_hr_cnt;
    if (hrsRaw != null) {
      const hrs = typeof hrsRaw === 'number' ? hrsRaw : Number(hrsRaw);
      return !isNaN(hrs) && hrs / 8 > longDurationDays;
    }
    // Fallback: duration/original_duration assumed to be in days
    const dur = (a as ScheduleActivity).duration ?? (a as ScheduleActivity).original_duration;
    const days = typeof dur === 'number' ? dur : dur != null ? Number(dur) : 0;
    return days > longDurationDays;
  }).length;
  const longDurationPct = total ? (longCount / total) * 100 : 0;
  const missingRelations = missingPredecessorsCount + missingSuccessorsCount;

  const checks: QualityCheckRow[] = [
    { id: 'high_float_pct', label: 'High float % (>44d)', value: Math.round(highFloatPct * 10) / 10, threshold: '≤15%', status: highFloatPct <= 15 ? 'pass' : highFloatPct <= 30 ? 'warn' : 'fail' },
    { id: 'long_duration_pct', label: 'Long duration % (>44d)', value: Math.round(longDurationPct * 10) / 10, threshold: '≤10%', status: longDurationPct <= 10 ? 'pass' : longDurationPct <= 20 ? 'warn' : 'fail' },
    { id: 'missing_predecessors', label: 'Missing predecessors', value: missingPredecessorsCount, threshold: '0', status: missingPredecessorsCount === 0 ? 'pass' : missingPredecessorsCount <= 3 ? 'warn' : 'fail' },
    { id: 'missing_successors', label: 'Missing successors', value: missingSuccessorsCount, threshold: '0', status: missingSuccessorsCount === 0 ? 'pass' : missingSuccessorsCount <= 3 ? 'warn' : 'fail' },
    { id: 'constraints_count', label: 'Constraints count', value: constraintsCount, threshold: '≤10', status: constraintsCount <= 10 ? 'pass' : 'warn' },
    { id: 'excessive_lags', label: 'Excessive lags', value: excessiveLagsCount, threshold: '≤5', status: excessiveLagsCount <= 5 ? 'pass' : 'warn' },
  ];
  return checks;
}

// ---------------------------------------------------------------------------
// S-curve and schedule chart computations (frontend fallbacks when API has no data)
// ---------------------------------------------------------------------------

/** Point for cumulative S-curve (date + cumulative count or hours) */
export interface SCurveCumulativePoint {
  date?: string | null;
  cumulative?: number;
}

/** S-curve activities: planned vs actual cumulative (monthly buckets).
 * Accepts both raw Parquet fields (task_type, target_end_date, act_end_date) and
 * activities API fields (type, target_finish_date, actual_finish_date). */
export function computeSCurveActivities(activities: ScheduleActivity[]): {
  planned: SCurveCumulativePoint[];
  actual: SCurveCumulativePoint[];
} {
  const plannedByMonth = new Map<string, number>();
  const actualByMonth = new Map<string, number>();
  const taskTypes = new Set(['TT_Task', 'TT_FinMile', 'TT_Mile', 'Task', 'Milestone', 'Finish Milestone']);
  const excludeTypes = new Set(['TT_LOE', 'TT_WBS', 'Level of Effort', 'WBS Summary']);
  for (const a of activities) {
    const raw = a as Record<string, unknown>;
    const taskType = (raw.task_type ?? raw.type) as string | undefined;
    if (taskType && excludeTypes.has(taskType)) continue;
    if (taskType && !taskTypes.has(taskType)) continue;

    const targetEnd = (a.target_end_date ?? a.target_finish_date ?? raw.target_end_date ?? raw.target_finish_date) as string | undefined;
    if (targetEnd && String(targetEnd).trim()) {
      const month = String(targetEnd).slice(0, 7);
      plannedByMonth.set(month, (plannedByMonth.get(month) ?? 0) + 1);
    }

    // Actual: count completed activities by actual finish date (API returns status "Completed", raw has status_code "TK_Complete")
    if (isCompleted(a)) {
      const actEnd = (raw.act_end_date ?? a.actual_finish_date ?? a.end_date ?? raw.actual_finish_date) as string | undefined;
      if (actEnd && String(actEnd).trim()) {
        const month = String(actEnd).slice(0, 7);
        actualByMonth.set(month, (actualByMonth.get(month) ?? 0) + 1);
      }
    }
  }
  const allMonths = [...new Set([...plannedByMonth.keys(), ...actualByMonth.keys()])].sort();
  let cumulPlanned = 0;
  let cumulActual = 0;
  const planned: SCurveCumulativePoint[] = allMonths.map((month) => {
    cumulPlanned += plannedByMonth.get(month) ?? 0;
    return { date: `${month}-01`, cumulative: cumulPlanned };
  });
  const actual: SCurveCumulativePoint[] = allMonths.map((month) => {
    cumulActual += actualByMonth.get(month) ?? 0;
    return { date: `${month}-01`, cumulative: cumulActual };
  });
  return { planned, actual };
}

/** S-curve duration: planned vs earned cumulative hours (monthly buckets).
 * Uses only target_drtn_hr_cnt (work hours from schedule). We do not convert duration from days,
 * since work hours per day depend on the activity calendar. */
export function computeSCurveDuration(activities: ScheduleActivity[]): {
  planned: SCurveCumulativePoint[];
  earned: SCurveCumulativePoint[];
} {
  const plannedByMonth = new Map<string, number>();
  const earnedByMonth = new Map<string, number>();
  for (const a of activities) {
    const raw = a as Record<string, unknown>;
    if (raw.task_type === 'TT_LOE' || raw.type === 'Level of Effort') continue;
    const hrsRaw = raw.target_drtn_hr_cnt;
    const hrs = hrsRaw != null ? (typeof hrsRaw === 'number' ? hrsRaw : Number(hrsRaw)) : 0;
    if (Number.isNaN(hrs) || hrs <= 0) continue;

    const targetEnd = (a.target_end_date ?? a.target_finish_date ?? raw.target_end_date ?? raw.target_finish_date) as string | undefined;
    if (targetEnd && String(targetEnd).trim()) {
      const month = String(targetEnd).slice(0, 7);
      plannedByMonth.set(month, (plannedByMonth.get(month) ?? 0) + hrs);
    }

    const pctRaw = a.phys_complete_pct ?? raw.phys_complete_pct ?? raw.physical_percent_complete;
    const pct = pctRaw != null ? (typeof pctRaw === 'number' ? pctRaw : Number(pctRaw)) : 0;
    const earnedHrs = hrs * (pct > 1 ? pct / 100 : pct);
    const earnedDate = (raw.act_end_date ?? a.actual_finish_date ?? a.early_end_date ?? a.end_date ?? raw.early_end_date ?? targetEnd) as string | undefined;
    if (earnedDate && String(earnedDate).trim()) {
      const month = String(earnedDate).slice(0, 7);
      earnedByMonth.set(month, (earnedByMonth.get(month) ?? 0) + earnedHrs);
    }
  }
  const allMonths = [...new Set([...plannedByMonth.keys(), ...earnedByMonth.keys()])].sort();
  let cumulPlanned = 0;
  let cumulEarned = 0;
  const planned: SCurveCumulativePoint[] = allMonths.map((month) => {
    cumulPlanned += plannedByMonth.get(month) ?? 0;
    return { date: `${month}-01`, cumulative: Math.round(cumulPlanned * 10) / 10 };
  });
  const earned: SCurveCumulativePoint[] = allMonths.map((month) => {
    cumulEarned += earnedByMonth.get(month) ?? 0;
    return { date: `${month}-01`, cumulative: Math.round(cumulEarned * 10) / 10 };
  });
  return { planned, earned };
}

/** Minimal WBS node for top-level grouping (optional; derived from activities if not provided) */
export interface WbsNodeForSCurve {
  wbs_id: string;
  parent_wbs_id?: string | null;
  wbs_name?: string;
}

/** Resolve root WBS id for an activity: use wbsNodes to find root, else use activity's wbs_id as root */
function getRootWbsId(
  activityWbsId: string,
  wbsNodes: WbsNodeForSCurve[] | undefined,
  wbsIdToNode: Map<string, WbsNodeForSCurve>
): string {
  if (!activityWbsId) return activityWbsId;
  if (!wbsNodes?.length) return activityWbsId;
  let current = wbsIdToNode.get(activityWbsId) ?? { wbs_id: activityWbsId, parent_wbs_id: null };
  const visited = new Set<string>();
  while (current?.parent_wbs_id && String(current.parent_wbs_id).trim() && !visited.has(current.wbs_id)) {
    visited.add(current.wbs_id);
    current = wbsIdToNode.get(String(current.parent_wbs_id)) ?? { wbs_id: current.parent_wbs_id, parent_wbs_id: null };
  }
  return current?.wbs_id ?? activityWbsId;
}

/** S-curve WBS: one series per top-level WBS, planned/earned per month */
export function computeSCurveWBS(
  activities: ScheduleActivity[],
  wbsNodes?: WbsNodeForSCurve[]
): { series: Array<{ wbs_id?: string; wbs_name?: string; points: Array<{ date?: string; planned?: number; earned?: number }> }> } {
  const byRoot = new Map<string, { planned: Map<string, number>; earned: Map<string, number>; wbsName: string }>();
  const wbsIdToNode = new Map<string, WbsNodeForSCurve>();
  if (wbsNodes?.length) {
    wbsNodes.forEach((n) => wbsIdToNode.set(n.wbs_id, n));
  }
  for (const a of activities) {
    const raw = a as Record<string, unknown>;
    if (raw.task_type === 'TT_LOE' || raw.type === 'Level of Effort') continue;
    const wbsId = String(a.wbs_id ?? raw.wbs_id ?? '');
    if (!wbsId) continue;
    const rootId = getRootWbsId(wbsId, wbsNodes, wbsIdToNode);
    if (!byRoot.has(rootId)) {
      const name = wbsNodes?.length ? (wbsIdToNode.get(rootId)?.wbs_name ?? rootId) : (a.wbs_name ?? raw.wbs_name ?? rootId);
      byRoot.set(rootId, { planned: new Map(), earned: new Map(), wbsName: String(name ?? rootId) });
    }
    const entry = byRoot.get(rootId)!;
    const hrsRaw = raw.target_drtn_hr_cnt;
    const hrs = hrsRaw != null ? (typeof hrsRaw === 'number' ? hrsRaw : Number(hrsRaw)) : 0;
    if (Number.isNaN(hrs)) continue;
    const pctRaw = a.phys_complete_pct ?? raw.phys_complete_pct ?? raw.physical_percent_complete;
    const pct = pctRaw != null ? (typeof pctRaw === 'number' ? pctRaw : Number(pctRaw)) : 0;
    const earnedHrs = hrs * (pct > 1 ? pct / 100 : pct);
    const targetEnd = (a.target_end_date ?? a.target_finish_date ?? raw.target_end_date ?? raw.target_finish_date) as string | undefined;
    if (targetEnd && String(targetEnd).trim()) {
      const month = String(targetEnd).slice(0, 7);
      entry.planned.set(month, (entry.planned.get(month) ?? 0) + hrs);
    }
    const earnedDate = (raw.act_end_date ?? a.actual_finish_date ?? a.early_end_date ?? a.end_date ?? raw.early_end_date ?? targetEnd) as string | undefined;
    if (earnedDate && String(earnedDate).trim()) {
      const month = String(earnedDate).slice(0, 7);
      entry.earned.set(month, (entry.earned.get(month) ?? 0) + earnedHrs);
    }
  }
  const series: Array<{ wbs_id?: string; wbs_name?: string; points: Array<{ date?: string; planned?: number; earned?: number }> }> = [];
  byRoot.forEach((data, rootId) => {
    const allMonths = [...new Set([...data.planned.keys(), ...data.earned.keys()])].sort();
    let cumulPlanned = 0;
    let cumulEarned = 0;
    const points = allMonths.map((month) => {
      cumulPlanned += data.planned.get(month) ?? 0;
      cumulEarned += data.earned.get(month) ?? 0;
      return {
        date: `${month}-01`,
        planned: Math.round(cumulPlanned * 10) / 10,
        earned: Math.round(cumulEarned * 10) / 10,
      };
    });
    series.push({ wbs_id: rootId, wbs_name: data.wbsName, points });
  });
  return { series };
}

/** Milestone on-time: by period (month), on_time_pct = count(act_end <= target_end) / total in period.
 * Accepts API shape: target_finish_date, actual_finish_date, status (e.g. "Completed"). */
export function computeMilestoneOnTime(activities: ScheduleActivity[]): {
  periods: Array<{ period?: string; on_time_pct?: number; total?: number; on_time_count?: number }>;
} {
  const milestones = activities.filter(isMilestone);
  const byPeriod = new Map<string, { total: number; onTime: number }>();
  for (const a of milestones) {
    if (!isCompleted(a)) continue;
    const raw = a as Record<string, unknown>;
    const actEnd = (raw.act_end_date ?? a.actual_finish_date ?? a.end_date ?? raw.actual_finish_date) as string | undefined;
    if (!actEnd || !String(actEnd).trim()) continue;
    const targetEnd = (a.target_end_date ?? a.target_finish_date ?? raw.target_end_date ?? raw.target_finish_date) as string | undefined;
    if (!targetEnd || !String(targetEnd).trim()) continue;
    const period = String(targetEnd).slice(0, 7);
    if (!byPeriod.has(period)) byPeriod.set(period, { total: 0, onTime: 0 });
    const entry = byPeriod.get(period)!;
    entry.total += 1;
    try {
      if (new Date(actEnd) <= new Date(targetEnd)) entry.onTime += 1;
    } catch {
      entry.onTime += 1;
    }
  }
  const periods = [...byPeriod.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([period, { total, onTime }]) => ({
      period,
      on_time_pct: total > 0 ? Math.round((100 * onTime) / total * 10) / 10 : 0,
      total,
      on_time_count: onTime,
    }));
  return { periods };
}
